import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CandidateProps } from "../types/candidate/candidate";
import { toast } from "sonner";
import { api } from "../services/api.service";

interface FormData {
  name?: string;
  sort?: string;
  loading?: boolean;
  emailverified?: string;
  setLoading?: (loading: boolean) => void;
}

interface candidateState {
  candidates: CandidateProps[];
  candidate: CandidateProps | null;
  noOfCandidates: number;
  noOfVerifiedCandidates: number;
  loading: boolean;
  error: string | null;
}

const initialState: candidateState = {
  candidates: [],
  candidate: null,
  noOfCandidates: 0,
  noOfVerifiedCandidates: 0,
  loading: false,
  error: null,
};

export const fetchCandidates = createAsyncThunk<CandidateProps[], FormData>(
  "candidate/fetchcandidates",
  async ({ name, sort, setLoading, emailverified }) => {
    const params: any = {};
    if (name) params.name = name;
    if (sort) params.sort = sort;
    if (emailverified) params.emailverified = emailverified;

    try {
      if (setLoading) setLoading(true);
      const data = await api.candidate.getAll(params);
      return data as CandidateProps[];
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    } finally {
      if (setLoading) setLoading(false);
    }
  }
);
export const fetchNoOfCandidates = createAsyncThunk<number>(
  "candidate/fetchNoOfCandidates",
  async () => {
    const data = await api.candidate.count();
    return data as number;
  }
);
export const fetchNoOfVerifiedCandidates = createAsyncThunk<number>(
  "candidate/fetchNoOfVerifiedCandidates",
  async () => {
    const data = await api.candidate.countVerified();
    return data as number;
  }
);
export const fetchCandidate = createAsyncThunk<CandidateProps, { id: number }>(
  "candidate/fetchcandidate",
  async ({ id }) => {
    const data = await api.candidate.getById(id);
    return data as CandidateProps;
  }
);

export const candidateSlice = createSlice({
  name: "candidate",
  initialState,
  reducers: {
    addCandidate: (state, action: PayloadAction<CandidateProps>) => {
      state.candidates.push(action.payload);
    },
    addCandidateCount: (state, action: PayloadAction<number>) => {
      state.noOfCandidates = action.payload;
    },

    removeCandidate: (state, action: PayloadAction<CandidateProps>) => {
      const id = action?.payload?.id?.toString()
      state.candidates = state.candidates.filter(
        (candidate) => candidate.id !== id
      );
    },
    updateCandidate: (state, action: PayloadAction<CandidateProps>) => {
      state.candidates = state.candidates.map((candidate) =>
        candidate.id === action.payload.id ? action.payload : candidate
      );
      state.candidate = action.payload;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        console.log("Fetched candidates:", action.payload);

        // Map API response fields to expected format
        const mappedCandidates = action.payload.map((candidate: any) => ({
          ...candidate,
          isActive: candidate.status === 'active' ? 1 : 0,
          AccountStatus: candidate.status === 'active' ? 'ACTIVE' : 'INACTIVE',
          // Keep both formats for compatibility
          status: candidate.status,
        }));

        state.candidates = mappedCandidates;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchCandidate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidate.fulfilled, (state, action) => {
        // Map API response fields to expected format
        const mappedCandidate = {
          ...action.payload,
          isActive: (action.payload as any).status === 'active' ? 1 : 0,
          AccountStatus: ((action.payload as any).status === 'active' ? 'ACTIVE' : 'INACTIVE') as "ACTIVE" | "INACTIVE",
        };

        state.candidate = mappedCandidate;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCandidate.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchNoOfCandidates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNoOfCandidates.fulfilled, (state, action) => {
        state.noOfCandidates = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfCandidates.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchNoOfVerifiedCandidates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNoOfVerifiedCandidates.fulfilled, (state, action) => {
        state.noOfVerifiedCandidates = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfVerifiedCandidates.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      });
  },
});

export const {
  addCandidate,
  removeCandidate,
  updateCandidate,
  addCandidateCount,
} = candidateSlice.actions;

export default candidateSlice.reducer;
